import React, { useState, useEffect } from 'react';
import {
  Card, Row, Col, Alert, Typography, Spin, Button,
  Table, Tag, Progress, Space, Tooltip, Badge, Divider
} from 'antd';
import {
  DatabaseOutlined, DashboardOutlined,
  ReloadOutlined, CheckCircleOutlined, ExclamationCircleOutlined,
  WarningOutlined, InfoCircleOutlined,
  ThunderboltOutlined, HddOutlined, ApiOutlined, RiseOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { hasPermission } from '../../utils/rolePermissions';
import logger from '../../services/logger';
import { safeFormatTimestamp } from '../../utils/timezone';

const { Title, Text } = Typography;

const MonitoringDashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [healthData, setHealthData] = useState(null);
  const [metricsData, setMetricsData] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [errors, setErrors] = useState({});

  const fetchHealthData = async () => {
    try {
      const response = await axios.get('/api/v1/health/detailed');
      setHealthData(response.data);
      setLastUpdate(new Date());
      setErrors(prev => ({ ...prev, health: null }));
    } catch (error) {
      logger.error('Health data fetch error:', error);
      setErrors(prev => ({ ...prev, health: 'Health verileri yüklenirken hata oluştu' }));
    }
  };

  const fetchMetricsData = async () => {
    try {
      const response = await axios.get('/api/v1/health/metrics');
      setMetricsData(response.data);
      setErrors(prev => ({ ...prev, metrics: null }));
    } catch (error) {
      logger.error('Metrics data fetch error:', error);
      setErrors(prev => ({ ...prev, metrics: 'Metrics verileri yüklenirken hata oluştu' }));
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    await Promise.all([fetchHealthData(), fetchMetricsData()]);
    setLoading(false);
  };

  useEffect(() => {
    fetchAllData();

    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchAllData, 30000);
    return () => clearInterval(interval);
  }, []);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy':
        return <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '16px' }} />;
      case 'warning':
        return <WarningOutlined style={{ color: '#faad14', fontSize: '16px' }} />;
      case 'unhealthy':
        return <ExclamationCircleOutlined style={{ color: '#ff4d4f', fontSize: '16px' }} />;
      default:
        return <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: '16px' }} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy':
        return 'success';
      case 'warning':
        return 'warning';
      case 'unhealthy':
        return 'error';
      default:
        return 'default';
    }
  };


  if (!hasPermission(user?.role, 'monitoring.read')) {
    return (
      <Alert
        message="Erişim Reddedildi"
        description="Monitoring dashboard'a erişim yetkiniz bulunmamaktadır."
        type="error"
        showIcon
      />
    );
  }

  if (loading && !healthData) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>
          <Text>Monitoring verileri yükleniyor...</Text>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={2} style={{ margin: 0 }}>
          <DashboardOutlined /> Monitoring
        </Title>
        <Space size="middle">
          {healthData && (
            <Space size="small">
              {getStatusIcon(healthData.status)}
              <Text strong style={{ color: healthData.status === 'healthy' ? '#52c41a' : '#faad14' }}>
                {healthData.status.toUpperCase()}
              </Text>
            </Space>
          )}
          {lastUpdate && (
            <Text type="secondary" style={{ fontSize: '13px' }}>
              {lastUpdate.toLocaleTimeString('tr-TR')}
            </Text>
          )}
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchAllData}
            loading={loading}
            size="middle"
          >
            Yenile
          </Button>
        </Space>
      </div>

      {/* Error Alerts */}
      {Object.entries(errors).map(([key, message]) => message && (
        <Alert
          key={key}
          message={message}
          type="error"
          showIcon
          closable
          onClose={() => setErrors(prev => ({ ...prev, [key]: null }))}
          style={{ marginBottom: '12px' }}
        />
      ))}

      {/* Hero Metrics - 4 Column */}
      <Row gutter={12} style={{ marginBottom: '12px' }}>
        <Col xs={12} sm={12} md={6} lg={6}>
          <Card
            size="small"
            style={{
              textAlign: 'center',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none'
            }}
            bodyStyle={{ padding: '16px 12px' }}
          >
            <DatabaseOutlined style={{ fontSize: '28px', color: '#fff', marginBottom: '8px' }} />
            <div style={{ color: '#fff' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', lineHeight: 1 }}>
                {metricsData?.metrics?.database?.device_count || 0}
              </div>
              <div style={{ fontSize: '13px', marginTop: '4px', opacity: 0.9 }}>
                Cihaz
              </div>
              {metricsData?.metrics?.database?.device_count > 0 && (
                <div style={{ fontSize: '12px', marginTop: '4px', opacity: 0.8 }}>
                  <RiseOutlined /> Aktif sistemde
                </div>
              )}
            </div>
          </Card>
        </Col>

        <Col xs={12} sm={12} md={6} lg={6}>
          <Card
            size="small"
            style={{
              textAlign: 'center',
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              border: 'none'
            }}
            bodyStyle={{ padding: '16px 12px' }}
          >
            <ThunderboltOutlined style={{ fontSize: '28px', color: '#fff', marginBottom: '8px' }} />
            <div style={{ color: '#fff' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', lineHeight: 1 }}>
                {metricsData?.metrics?.system?.cpu_percent ? Math.round(metricsData.metrics.system.cpu_percent) : 0}%
              </div>
              <div style={{ fontSize: '13px', marginTop: '4px', opacity: 0.9 }}>
                CPU Kullanımı
              </div>
              <div style={{ fontSize: '12px', marginTop: '4px', opacity: 0.8 }}>
                {metricsData?.metrics?.system?.cpu_percent > 80 ? '⚠ Yüksek' : '✓ Normal'}
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={12} sm={12} md={6} lg={6}>
          <Card
            size="small"
            style={{
              textAlign: 'center',
              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              border: 'none'
            }}
            bodyStyle={{ padding: '16px 12px' }}
          >
            <HddOutlined style={{ fontSize: '28px', color: '#fff', marginBottom: '8px' }} />
            <div style={{ color: '#fff' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', lineHeight: 1 }}>
                {metricsData?.metrics?.system?.memory_percent ? Math.round(metricsData.metrics.system.memory_percent) : 0}%
              </div>
              <div style={{ fontSize: '13px', marginTop: '4px', opacity: 0.9 }}>
                Bellek
              </div>
              <div style={{ fontSize: '12px', marginTop: '4px', opacity: 0.8 }}>
                {metricsData?.metrics?.system?.memory_available_gb || 0} GB boş
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={12} sm={12} md={6} lg={6}>
          <Card
            size="small"
            style={{
              textAlign: 'center',
              background: metricsData?.metrics?.mqtt_backend?.connected
                ? 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)'
                : 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
              border: 'none'
            }}
            bodyStyle={{ padding: '16px 12px' }}
          >
            <ApiOutlined style={{ fontSize: '28px', color: '#fff', marginBottom: '8px' }} />
            <div style={{ color: '#fff' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', lineHeight: 1 }}>
                {metricsData?.metrics?.mqtt_backend?.connected ? 'Bağlı' : 'Kapalı'}
              </div>
              <div style={{ fontSize: '13px', marginTop: '4px', opacity: 0.9 }}>
                MQTT Broker
              </div>
              <div style={{ fontSize: '12px', marginTop: '4px', opacity: 0.8 }}>
                {metricsData?.metrics?.mqtt_device?.active_topics?.length || 0} topic
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Main Content - 3 Columns */}
      <Row gutter={12} style={{ marginBottom: '12px' }}>
        {/* Database Column */}
        <Col xs={24} sm={24} md={8} lg={8}>
          <Card
            title={
              <Space size="small">
                <DatabaseOutlined />
                <span>Database</span>
              </Space>
            }
            size="small"
            bodyStyle={{ padding: '12px 16px' }}
          >
            <Space direction="vertical" style={{ width: '100%' }} size={8}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text type="secondary" style={{ fontSize: '13px' }}>Cihazlar</Text>
                <Text strong>{metricsData?.metrics?.database?.device_count || 0}</Text>
              </div>
              <Divider style={{ margin: '4px 0' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text type="secondary" style={{ fontSize: '13px' }}>Telemetri</Text>
                <Text strong>{metricsData?.metrics?.database?.telemetry_records?.toLocaleString() || 0}</Text>
              </div>
              <Divider style={{ margin: '4px 0' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text type="secondary" style={{ fontSize: '13px' }}>Loglar</Text>
                <Text strong>{metricsData?.metrics?.database?.log_records?.toLocaleString() || 0}</Text>
              </div>
              <Divider style={{ margin: '8px 0' }} />
              <div>
                <Text type="secondary" style={{ fontSize: '13px' }}>TimescaleDB</Text>
                <div style={{ marginTop: '6px' }}>
                  <Badge
                    status={healthData?.components?.timescaledb?.status === 'healthy' ? 'success' : 'error'}
                    text={healthData?.components?.timescaledb?.status?.toUpperCase() || 'N/A'}
                  />
                </div>
                {healthData?.components?.timescaledb?.hypertables && (
                  <div style={{ marginTop: '6px' }}>
                    <Text style={{ fontSize: '12px' }}>
                      {healthData.components.timescaledb.hypertables.length} hypertable
                    </Text>
                  </div>
                )}
                <div style={{ marginTop: '4px' }}>
                  <Text style={{ fontSize: '12px' }} type="secondary">
                    Saklama: {healthData?.components?.timescaledb?.retention_policy || 'N/A'}
                  </Text>
                </div>
              </div>
            </Space>
          </Card>
        </Col>

        {/* System Resources Column */}
        <Col xs={24} sm={24} md={8} lg={8}>
          <Card
            title={
              <Space size="small">
                <ThunderboltOutlined />
                <span>Sistem Kaynakları</span>
              </Space>
            }
            size="small"
            bodyStyle={{ padding: '12px 16px' }}
          >
            <Space direction="vertical" style={{ width: '100%' }} size={10}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                  <Text type="secondary" style={{ fontSize: '13px' }}>CPU</Text>
                  <Text strong>{metricsData?.metrics?.system?.cpu_percent ? Math.round(metricsData.metrics.system.cpu_percent) : 0}%</Text>
                </div>
                <Progress
                  percent={metricsData?.metrics?.system?.cpu_percent ? Math.round(metricsData.metrics.system.cpu_percent) : 0}
                  status={metricsData?.metrics?.system?.cpu_percent > 80 ? 'exception' : 'normal'}
                  showInfo={false}
                  strokeWidth={6}
                  strokeColor={{
                    '0%': '#108ee9',
                    '100%': '#87d068',
                  }}
                />
              </div>

              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                  <Text type="secondary" style={{ fontSize: '13px' }}>Bellek</Text>
                  <Text strong>{metricsData?.metrics?.system?.memory_percent ? Math.round(metricsData.metrics.system.memory_percent) : 0}%</Text>
                </div>
                <Progress
                  percent={metricsData?.metrics?.system?.memory_percent ? Math.round(metricsData.metrics.system.memory_percent) : 0}
                  status={metricsData?.metrics?.system?.memory_percent > 80 ? 'exception' : 'normal'}
                  showInfo={false}
                  strokeWidth={6}
                  strokeColor={{
                    '0%': '#1890ff',
                    '100%': '#52c41a',
                  }}
                />
                <Text style={{ fontSize: '12px' }} type="secondary">
                  {metricsData?.metrics?.system?.memory_available_gb || 0} GB boş
                </Text>
              </div>

              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                  <Text type="secondary" style={{ fontSize: '13px' }}>Disk</Text>
                  <Text strong>{metricsData?.metrics?.system?.disk_percent ? Math.round(metricsData.metrics.system.disk_percent) : 0}%</Text>
                </div>
                <Progress
                  percent={metricsData?.metrics?.system?.disk_percent ? Math.round(metricsData.metrics.system.disk_percent) : 0}
                  status={metricsData?.metrics?.system?.disk_percent > 80 ? 'exception' : 'normal'}
                  showInfo={false}
                  strokeWidth={6}
                  strokeColor={{
                    '0%': '#722ed1',
                    '100%': '#eb2f96',
                  }}
                />
                <Text style={{ fontSize: '12px' }} type="secondary">
                  {metricsData?.metrics?.system?.disk_free_gb || 0} GB boş
                </Text>
              </div>
            </Space>
          </Card>
        </Col>

        {/* MQTT Column */}
        <Col xs={24} sm={24} md={8} lg={8}>
          <Card
            title={
              <Space size="small">
                <ApiOutlined />
                <span>MQTT</span>
              </Space>
            }
            size="small"
            bodyStyle={{ padding: '12px 16px' }}
          >
            <Space direction="vertical" style={{ width: '100%' }} size={8}>
              <div>
                <Text strong style={{ fontSize: '13px' }}>Backend</Text>
                <div style={{ marginTop: '6px' }}>
                  <Badge
                    status={metricsData?.metrics?.mqtt_backend?.connected ? 'success' : 'error'}
                    text={metricsData?.metrics?.mqtt_backend?.connected ? 'Bağlı' : 'Kapalı'}
                  />
                </div>
                <Text style={{ fontSize: '12px' }} type="secondary">
                  {metricsData?.metrics?.mqtt_backend?.host || 'N/A'}:{metricsData?.metrics?.mqtt_backend?.port || 'N/A'}
                </Text>
              </div>

              <Divider style={{ margin: '8px 0' }} />

              <div>
                <Text strong style={{ fontSize: '13px' }}>Device</Text>
                <div style={{ marginTop: '6px' }}>
                  <Badge
                    status={metricsData?.metrics?.mqtt_device?.tls_enabled ? 'success' : 'warning'}
                    text={metricsData?.metrics?.mqtt_device?.tls_enabled ? 'TLS Aktif' : 'TLS Pasif'}
                  />
                </div>
                <Text style={{ fontSize: '12px' }} type="secondary">
                  {metricsData?.metrics?.mqtt_device?.host || 'N/A'}:{metricsData?.metrics?.mqtt_device?.port || 'N/A'}
                </Text>
              </div>

              <Divider style={{ margin: '8px 0' }} />

              <div>
                <Text type="secondary" style={{ fontSize: '13px' }}>Aktif Topic'ler</Text>
                <div style={{ marginTop: '6px' }}>
                  {metricsData?.metrics?.mqtt_device?.active_topics?.length > 0 ? (
                    metricsData.metrics.mqtt_device.active_topics.map((topic, index) => (
                      <Tag key={index} color="blue" style={{ fontSize: '11px', marginBottom: '4px' }}>
                        {topic}
                      </Tag>
                    ))
                  ) : (
                    <Text type="secondary" style={{ fontSize: '12px' }}>Yok</Text>
                  )}
                </div>
              </div>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* Component Status Table */}
      <Card
        title={
          <Space size="small">
            <InfoCircleOutlined />
            <span>Bileşen Durumları</span>
          </Space>
        }
        size="small"
        bodyStyle={{ padding: '8px' }}
      >
        {healthData?.components ? (
          <Table
            dataSource={Object.entries(healthData.components).map(([key, value]) => ({
              key,
              component: key,
              status: value.status,
              message: value.message,
              details: value
            }))}
            columns={[
              {
                title: 'Bileşen',
                dataIndex: 'component',
                key: 'component',
                width: '25%',
                render: (text) => (
                  <Text strong style={{ fontSize: '13px' }}>
                    {text.toUpperCase()}
                  </Text>
                )
              },
              {
                title: 'Durum',
                dataIndex: 'status',
                key: 'status',
                width: '15%',
                render: (status) => (
                  <Badge
                    status={status === 'healthy' ? 'success' : status === 'warning' ? 'warning' : 'error'}
                    text={<span style={{ fontSize: '13px' }}>{status.toUpperCase()}</span>}
                  />
                )
              },
              {
                title: 'Mesaj',
                dataIndex: 'message',
                key: 'message',
                render: (text) => <Text style={{ fontSize: '13px' }}>{text}</Text>
              },
              {
                title: '',
                key: 'details',
                width: 60,
                align: 'center',
                render: (_, record) => (
                  <Tooltip title={<pre style={{ fontSize: '11px' }}>{JSON.stringify(record.details, null, 2)}</pre>}>
                    <Button type="text" size="small" icon={<InfoCircleOutlined />} />
                  </Tooltip>
                )
              }
            ]}
            pagination={false}
            size="small"
          />
        ) : (
          <Text type="secondary">Veri yükleniyor...</Text>
        )}
      </Card>
    </div>
  );
};

export default MonitoringDashboard;
